import axios, { AxiosInstance } from 'axios';

import { env } from '../utils/env';
import LS from '../utils/localStorageUtils';

export const api: AxiosInstance = axios.create({
	baseURL: env('SVARA_API_URL'),
});

api.interceptors.request.use(config => {
	const token = LS.get('access_token');
	if (token) {
		(config.headers as any) = {
			...(config.headers || {}),
			Authorization: `Bearer ${token}`,
		};
	}
	return config;
});

export type TableDescriptor = {
	/**
	 * Numeric player id of whoever created the table, or null for the public rooms
	 * which have no owner. Numeric because the Svara client compares it against
	 * initData.userId, which is also the numeric id.
	 */
	owner: number | null;
	ownerUsername: string | null;
	isLocked: boolean;
};

/**
 * A single table's public descriptor. The game host needs the owner before it can
 * call startGame, because initData.ownerId is what gates the owner-only
 * affordances at a table (inviting into an empty seat, kicking a player). v1 did
 * the same lookup through GET /tables/by_name; v2 addresses tables by id.
 *
 * This is the only surviving helper here: the rest of this file used bare paths
 * (/tables/..., /rooms/...) that 404 in every environment — the gateway serves
 * them under /api/lobby and nothing rewrites (verified locally and on stage).
 * The live app's REST goes through services/api.ts, which prefixes correctly.
 */
export async function getTableDescriptor(tableId: string | number): Promise<TableDescriptor> {
	const { data: response } = await api.get(
		`/api/lobby/tables/${encodeURIComponent(String(tableId))}`,
	);
	const table = response?.data ?? {};
	const owner = table.owner;

	return {
		owner: typeof owner === 'number' ? owner : owner == null ? null : Number(owner) || null,
		ownerUsername: table.owner_username ?? null,
		isLocked: table.is_locked === 1 || table.is_locked === true,
	};
}
